import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { PRODUCT_OPTIONS } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full Name *", type: "text", required: true, half: true },
  { name: "company", label: "Company", type: "text", half: true },
  { name: "phone", label: "Phone *", type: "tel", required: true, half: true },
  { name: "email", label: "Email *", type: "email", required: true, half: true },
  { name: "location", label: "City / State", type: "text", half: true },
  { name: "quantity", label: "Est. Monthly Volume", type: "text", half: true },
];

export const EnquiryForm = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "",
    location: "", product: params.get("product") || "General Enquiry",
    quantity: "", message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Enquiry received. Our team will contact you shortly.");
      setForm({
        name: "", company: "", phone: "", email: "",
        location: "", product: "General Enquiry", quantity: "", message: "",
      });
    } catch {
      toast.error("Could not send your enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-amber focus:bg-white/[0.07] outline-none transition-colors";
  const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2";

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="rfq-form">
      <div className="grid grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.half ? "col-span-2 sm:col-span-1" : "col-span-2"}>
            <label htmlFor={`rfq-${f.name}`} className={labelCls}>{f.label}</label>
            <input
              id={`rfq-${f.name}`}
              type={f.type}
              value={form[f.name]}
              onChange={set(f.name)}
              required={f.required}
              placeholder={f.name === "phone" ? "+91 ..." : ""}
              className={inputCls}
              data-testid={`rfq-input-${f.name}`}
            />
          </div>
        ))}
        <div className="col-span-2">
          <label htmlFor="rfq-product" className={labelCls}>Product of Interest</label>
          <select
            id="rfq-product"
            value={form.product}
            onChange={set("product")}
            className={`${inputCls} bg-deep appearance-none`}
            data-testid="rfq-select-product"
          >
            {PRODUCT_OPTIONS.map((o) => (
              <option key={o} value={o} className="bg-deep">{o}</option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label htmlFor="rfq-message" className={labelCls}>Message / Requirements</label>
          <textarea
            id="rfq-message"
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us about your fleet, sizes and delivery location..."
            className={`${inputCls} resize-none`}
            data-testid="rfq-input-message"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-signal px-10 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white hover:bg-signalhover disabled:opacity-60 transition-colors"
        data-testid="rfq-form-submit-button"
      >
        {sending ? "Sending..." : "Send Enquiry"}
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="font-mono text-[10px] text-slate-500 tracking-wider">
        We respond to every enquiry personally — no automated payment, no obligation.
      </p>
    </form>
  );
};

export default EnquiryForm;
